import { Alert } from '@ksyos/design-system';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { ValidationErrors } from '@frontend/utils/validation';
import { CareProductType, ManagerAuthorization, ValidationErrorCode } from '@common/enums/';
import {
    insurerTurnoverCeilingAddSchema,
    insurerTurnoverCeilingEditSchema,
} from '@common/validation/insurerTurnoverCeiling';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import { CancelButton, FormContainer, Header, InputCurrency, Loading, SaveSubmit } from '../../elements/common';
import { Component } from '../../elements/Component';
import { FormContextProvider } from '../../elements/formContext';
import { FormLabel } from '../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../elements/forms/InputContainer';
import { DropdownField } from '../../fields/Dropdown';
import { NumberField } from '../../fields/Number';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';

type InsurerTurnoverCeilingAddEditProps = IWithRouterProps;

interface IInsurerTurnoverCeiling {
    careProductType: CareProductType | null;
    ceilingAmount: number | null;
    id?: string;
    insurerId: string | null;
    year: number | null;
}

interface IOverlappedInsurerTurnoverCeiling {
    careProductType: CareProductType;
    year: number;
}

interface IInsurerTurnoverCeilingAddEditState {
    errorCode?: string;
    errors: ValidationErrors;
    insurerName: string | null;
    insurerTurnoverCeiling: IInsurerTurnoverCeiling;
    isSaving: boolean;
    overlap: IOverlappedInsurerTurnoverCeiling | null;
}

class InnerInsurerTurnoverCeilingAddEdit extends Component<
    InsurerTurnoverCeilingAddEditProps,
    IInsurerTurnoverCeilingAddEditState
> {
    constructor(props: InsurerTurnoverCeilingAddEditProps) {
        super(props);

        assertHasAuthorization(ManagerAuthorization.insurersAddEdit);

        this.state = {
            insurerTurnoverCeiling: {
                year: null,
                ceilingAmount: null,
                insurerId: '',
                careProductType: null,
            },
            isSaving: false,
            errors: emptyValidationErrors,
            overlap: null,
            insurerName: null,
        };
    }

    public async componentDidMount() {
        if (this.props.router.params.id) {
            this.load();
        } else {
            await this.setState(({ insurerTurnoverCeiling }) => ({
                insurerTurnoverCeiling: {
                    ...insurerTurnoverCeiling,
                    insurerId: this.props.router.searchParams.get('insurerId'),
                },
            }));
        }

        await this.loadInsurerName();
    }

    public render() {
        return (
            <FixedWidthTemplate>
                <Header
                    content={
                        translateKey('insurers.turnoverCeiling') +
                        ' ' +
                        (this.props.router.params.id ? translateKey('common.edit') : translateKey('common.new'))
                    }
                />

                {this.state.errorCode && this.state.overlap && this.state.insurerName && (
                    <Alert appearance="warning">
                        {translateKey(
                            'insurers.ovelappedTurnoverCeiling',
                            this.state.insurerName,
                            this.state.overlap.careProductType,
                            this.state.overlap.year.toString()
                        )}
                    </Alert>
                )}

                <Loading
                    item={this.state.insurerTurnoverCeiling}
                    loaded={(insurerTurnoverCeiling) => (
                        <FormContainer onSubmit={async () => await this.save()}>
                            <FormContextProvider
                                value={{
                                    placeholderGroupName: '',
                                    item: this.state.insurerTurnoverCeiling,
                                    errors: this.state.errors,
                                    onChange: async (key, value) => await this.handleChange(key, value),
                                    onDoValidate: async (key) => await this.validateOne(key),
                                }}
                            >
                                <NumberField
                                    fieldKey="year"
                                    isRequired={true}
                                    label={translateKey('insurers.year')}
                                    placeholder={translateKey('insurers.year')}
                                />
                                <HorizontalFormGroup hasErrors={this.state.errors.has('ceilingAmount')}>
                                    <FormLabel isRequired={true}>{translateKey('insurers.ceilingAmount')}</FormLabel>
                                    <InputContainer>
                                        <InputCurrency
                                            error={this.state.errors.get('ceilingAmount')}
                                            fieldKey="ceilingAmount"
                                            placeholder={translateKey('insurers.ceilingAmount')}
                                            value={this.state.insurerTurnoverCeiling.ceilingAmount}
                                        />
                                    </InputContainer>
                                </HorizontalFormGroup>

                                <DropdownField
                                    fieldKey="careProductType"
                                    label={translateKey('insurers.careProductType')}
                                    placeholder={translateKey(
                                        'common.selectItem',
                                        translateKey('insurers.careProductType')
                                    )}
                                    selectOptions={this.getCareProductTypeOptions()}
                                />
                            </FormContextProvider>

                            <CancelButton
                                disabled={this.state.isSaving}
                                to={`/insurers/${this.getQueryInsurerId()}/edit`}
                            />
                            <SaveSubmit disabled={this.state.isSaving} />
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async handleChange<K extends keyof IInsurerTurnoverCeiling>(name: K, value: IInsurerTurnoverCeiling[K]) {
        await this.setState(({ insurerTurnoverCeiling }) => ({
            insurerTurnoverCeiling: { ...insurerTurnoverCeiling, [name]: value },
        }));
    }

    private async validateOne(name: keyof IInsurerTurnoverCeiling) {
        await this.setState(({ insurerTurnoverCeiling, errors }) => ({
            errors: updateValidationErrorsForOne(insurerTurnoverCeilingAddSchema, insurerTurnoverCeiling, errors, name),
        }));
    }

    private async load() {
        const url = '/api/insurerTurnoverCeilings/' + this.props.router.params.id;
        const result = await fetchJson(url, 'GET');
        if (result.json) {
            await this.setState({ insurerTurnoverCeiling: result.json });
        }
    }

    private async loadInsurerName() {
        const url = '/api/insurers/' + this.getQueryInsurerId();
        const result = await fetchJson(url, 'GET');
        await this.setState({ insurerName: result.json.name });
    }

    private async save() {
        let url = '/api/insurerTurnoverCeilings/';
        if (this.props.router.params.id) {
            url += this.props.router.params.id;
        }

        const { errors, item } = validate(
            this.props.router.params.id ? insurerTurnoverCeilingEditSchema : insurerTurnoverCeilingAddSchema,
            this.state.insurerTurnoverCeiling
        );
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }
        if (item) {
            await this.setState({ insurerTurnoverCeiling: item, isSaving: true });
        }

        const result = await fetchJson(url, 'POST', item, [ValidationErrorCode.OVERLAPPING_CONFIG]);

        if (result.errorCode && item) {
            await this.setState({
                isSaving: false,
                errorCode: result.errorCode,
                overlap: item as IOverlappedInsurerTurnoverCeiling,
            });
        } else {
            const returnUrl = `/insurers/${this.getQueryInsurerId()}/edit`;
            this.props.router.navigate(returnUrl);
        }
    }

    private getQueryInsurerId() {
        return this.props.router.searchParams.get('insurerId');
    }

    private getCareProductTypeOptions() {
        return [
            { text: CareProductType.DBC, value: CareProductType.DBC },
            { text: CareProductType.MKC, value: CareProductType.MKC },
            { text: CareProductType.OVP, value: CareProductType.OVP },
        ];
    }
}

export const InsurerTurnoverCeilingAddEdit = withRouter(InnerInsurerTurnoverCeilingAddEdit);
